// COMING-UP REBUILD (2026-07-03, QJ-ratified original concept): a day-grouped agenda MIXING
// interactive TeeUp cards with plain calendar events, chronological within each day.
// - DAY SECTIONS: "TODAY" / "TOMORROW" / "TUESDAY, DECEMBER 18" headers; empty days collapse
//   (we only ever render days that have items).
// - TEEUP CARDS (rich, click -> opens the TeeUp): cover image left, status chip (ALL SET /
//   PLANNING -- blue-family per Ale's palette, semantically distinct), title, date+time, place,
//   participant avatar row with "-> N", the user's OWN status badge (GOING / MIGHT GO), and a
//   calendar-sync icon on decided TeeUps when a write-target calendar is connected.
// - CALENDAR EVENTS (plain, non-interactive): slim rows with a colored left bar, title +
//   "3:00 PM - 5:00 PM" range, interleaved with TeeUp cards in time order.
//   Source: the CALFIX-3 server-side events proxy (GET /v1/users/calendars/{id}/events) for the
//   calendars the user selected for display (settings.calendarSync.selectedCalendars, same
//   matching rule P0FUNC ITEM1 established).
// - RIGHT RAIL (wide viewports only, CSS-collapsed below 1200px): "Connected calendars" summary
//   (SYNCING = write-target account/calendar; DISPLAYED = accounts + selected calendar names,
//   from the CALFIX-CAL2 grouped rows) + "Manage my calendars" -> Settings > Calendars.
import React, { useEffect, useState } from "react"

import moment from "moment-timezone"
import { useHistory } from "react-router-dom"

import { aliasId } from "@utils/devBackendShim"
import { images } from "@utils/imageUtils"
import { TEEUP_IMAGES } from "@config/teeupImages"

import api from "../../api"

import "./index.scss"

// COMING-UP FIX (2026-07-03, item 2): the Coming Up card cover must resolve EXACTLY like the TeeUp
// detail hero (TeeupOverviewHeader) and the Inbox card (TeeUpsNd/.../TeeUpPhoto) -- a real cover if
// present, otherwise a STABLE bundled default from TEEUP_IMAGES keyed by the same seed the detail
// uses (the aliased teeup id), and the SAME default on load error. So a TeeUp with a real cover
// shows the identical image on the card and the detail, and a no-cover TeeUp shows a clean bundled
// cover (never a grey box, never a broken-image icon). Replicates defaultCoverFor verbatim from
// those two components (the codebase already duplicates this 6-line helper in both, by design --
// it depends only on TEEUP_IMAGES, no shared module to import).
const asCoverUrl = img => (img && img.default) || img
const defaultCoverFor = seed => {
  const s = String(seed ?? "")
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return asCoverUrl(TEEUP_IMAGES[h % TEEUP_IMAGES.length])
}

// Card cover: same start-with-real / fall-to-bundled-default-on-error contract as TeeUpPhoto.
const CardCover = ({ src, seed }) => {
  const fallback = defaultCoverFor(seed)
  const [imgSrc, setImgSrc] = useState(src || fallback)
  useEffect(() => {
    setImgSrc(src || fallback)
  }, [src, fallback])
  return (
    <img
      className="cua-card__img"
      src={imgSrc}
      alt=""
      data-testid="cua-card-cover"
      onError={() => {
        if (imgSrc !== fallback) setImgSrc(fallback)
      }}
    />
  )
}

const EVENT_WINDOW_DAYS = 30
const MAX_EVENT_CALENDARS = 8
const MAX_AVATARS = 4

// blue-family bar colors, one per displayed calendar (cycled) -- keeps distinct sources visually
// separable without leaving Ale's palette.
const EVENT_BAR_COLORS = ["#7FB1E3", "#4A7FB5", "#9EC4EA", "#2F5E8F", "#BED8F1"]

const pickSuggestion = (co, dimension) => {
  const opt = (co.gameplan || []).find(g => g.dimension === dimension)
  if (!opt || !opt.suggestions || opt.suggestions.length === 0) return null
  const sugs = opt.suggestions
  return (
    sugs.find(sg => sg.is_decided && sg.value) ||
    (opt.status === "decided" && sugs.find(sg => sg.value)) ||
    sugs.find(sg => sg.value) ||
    null
  )
}

const dayLabel = m => {
  if (m.isSame(moment(), "day")) return "Today"
  if (m.isSame(moment().add(1, "day"), "day")) return "Tomorrow"
  return m.format("dddd, MMMM D")
}

const isAllDay = (start, end) =>
  start.format("HH:mm") === "00:00" && (!end || end.format("HH:mm") === "00:00")

const timeRange = (start, end, allDay) => {
  if (allDay) return "All Day"
  const fmt = t => t.format("h:mm A")
  return end && !end.isSame(start) ? `${fmt(start)} - ${fmt(end)}` : fmt(start)
}

const OWN_STATUS_LABELS = {
  going: "GOING",
  mightgo: "MIGHT GO",
  might_go: "MIGHT GO",
  notgoing: "NOT GOING",
  not_going: "NOT GOING",
  invited: "INVITED",
}

// COMING-UP FIX (2026-07-03): provider icon, same source images + provider->icon mapping as
// Settings > Calendars (CalendarSettingsLogoWrapper in components/more/calendarSettings) --
// windowslive = Outlook (office365 mark), apple = Apple, else Google.
const providerIcon = provider => {
  if (provider === "windowslive" || provider === "outlook") return images.office365
  if (provider === "apple") return images.appleIntegration
  return images.googleIntegration
}

const ProviderIcon = ({ provider }) => (
  <img
    className="cua-rail-card__provider-icon"
    src={providerIcon(provider)}
    alt=""
    data-testid="cua-provider-icon"
  />
)

// small inline calendar-sync glyph (Ale icon language: 24 viewBox, 1.7 stroke, currentColor)
const CalSyncIcon = () => (
  <svg
    className="cua-card__sync"
    data-testid="cua-cal-sync"
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M8 3v4M16 3v4M3 10h18" />
    <path d="M10.5 15.5l2 2 3.5-3.5" />
  </svg>
)

const ComingUpAgenda = () => {
  const [days, setDays] = useState([])
  const [rail, setRail] = useState(null) // {syncing:{account,calendar,provider}, displayed:[{account, provider, calendars:[]}]}
  const [loading, setLoading] = useState(true)
  const history = useHistory()

  useEffect(() => {
    let alive = true
    ;(async () => {
      const startOfToday = moment().startOf("day")

      // ---- TeeUps (the user's active coordination objects with a When) ----
      const teeupRows = []
      let hasWriteTarget = false
      try {
        const { data } = await api.client.get("/v1/users/me/coordination-objects", {
          params: { category: 1, limit: 500 },
        })
        const items = (data && data.items) || []
        items.forEach(co => {
          if (co.status === "ended" || co.status === "cancelled") return
          const whenSug = pickSuggestion(co, "when")
          const w = whenSug && whenSug.value
          if (!w || !w.start) return
          const start = moment(w.start)
          const end = w.end ? moment(w.end) : null
          if ((end || start).isBefore(startOfToday)) return
          const whereSug = pickSuggestion(co, "where")
          const decided = !!co.gameplan_decided || co.status === "scheduled" || !!co.is_scheduled
          const allDay = isAllDay(start, end)
          teeupRows.push({
            kind: "teeup",
            id: aliasId(co.id),
            title: co.name,
            start,
            allDay,
            photo: co.image_url || co.photo || null,
            decided,
            place: (whereSug && whereSug.value && whereSug.value.name) || null,
            dateTime: `${start.format("ddd, MMM D")} · ${timeRange(start, end, allDay)}`,
            participants: (co.participants || []).slice(0, MAX_AVATARS),
            participantCount: co.participant_count || (co.participants || []).length,
            ownStatus: OWN_STATUS_LABELS[String(co.my_status || "").toLowerCase()] || null,
          })
        })
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[coming-up] teeup load failed", e)
      }

      // ---- Connected calendars + display selection + events ----
      const eventRows = []
      try {
        // settings ride on GET /v1/users/me (there is no GET /users/me/settings on dev)
        const [calRes, meRes] = await Promise.all([
          api.client.get("/v1/users/calendars"),
          api.client.get("/v1/users/me"),
        ])
        const cals = (calRes.data && calRes.data.calendars) || []
        const settings = (meRes.data && meRes.data.settings) || {}
        const calendarSync = settings.calendarSync || {}
        const selected = calendarSync.selectedCalendars || []
        const isSelected = r =>
          selected.includes(r.calendar_id) ||
          selected.includes(r.email) ||
          selected.includes(r.id)

        const writeTarget = cals.find(r => r.is_write_target)
        hasWriteTarget = !!writeTarget
        const displayedCals = cals.filter(isSelected)

        // right-rail summary (CALFIX-CAL2 account grouping)
        const byAccount = []
        displayedCals.forEach(r => {
          const acct = r.account_email || r.email
          const g = byAccount.find(x => x.account === acct)
          const nm = r.name || r.email || r.calendar_id
          if (g) g.calendars.push(nm)
          else byAccount.push({ account: acct, provider: r.provider, calendars: [nm] })
        })
        // COMING-UP FIX (2026-07-03, item 3b): a primary Google/Outlook calendar's own `name` IS
        // the account email (that's the provider's convention, not a bug in this component) --
        // showing it under the ACCOUNT header duplicated the same string. Label that case
        // "Primary calendar" so the row reads as the CALENDAR being written to, distinct from the
        // account header above it. A non-primary write-target (a named secondary calendar) still
        // shows its real name unchanged.
        const writeTargetCalendarName =
          writeTarget &&
          (writeTarget.name || writeTarget.email) === (writeTarget.account_email || writeTarget.email)
            ? "Primary calendar"
            : writeTarget.name || writeTarget.email
        if (alive)
          setRail({
            connected: cals.length > 0,
            syncing: writeTarget
              ? {
                  account: writeTarget.account_email || writeTarget.email,
                  calendar: writeTargetCalendarName,
                  provider: writeTarget.provider,
                }
              : null,
            displayed: byAccount,
          })

        // events from each displayed calendar via the server-side proxy
        const from = startOfToday.toISOString()
        const to = moment().add(EVENT_WINDOW_DAYS, "days").endOf("day").toISOString()
        const colorByCal = {}
        displayedCals.forEach((r, i) => {
          colorByCal[r.id] = EVENT_BAR_COLORS[i % EVENT_BAR_COLORS.length]
        })
        const results = await Promise.all(
          displayedCals.slice(0, MAX_EVENT_CALENDARS).map(r =>
            api.client
              .get(`/v1/users/calendars/${r.id}/events`, { params: { from, to } })
              .then(res => ({ cal: r, items: (res.data && res.data.items) || [] }))
              .catch(() => ({ cal: r, items: [] }))
          )
        )
        results.forEach(({ cal, items }) => {
          items.forEach(ev => {
            // Google: {summary, start:{dateTime|date}, end:{...}} · Outlook: {subject, start:{dateTime}}
            const rawStart = ev.start && (ev.start.dateTime || ev.start.date)
            if (!rawStart) return
            const allDay = !!(ev.start && ev.start.date && !ev.start.dateTime)
            const start = moment(rawStart)
            const rawEnd = ev.end && (ev.end.dateTime || ev.end.date)
            // Google all-day ends are EXCLUSIVE (next midnight) -- pull back for display
            const end = rawEnd ? moment(rawEnd).subtract(allDay ? 1 : 0, "ms") : null
            if ((end || start).isBefore(startOfToday)) return
            eventRows.push({
              kind: "event",
              id: `ev-${cal.id}-${ev.id || rawStart}`,
              title: ev.summary || ev.subject || "(no title)",
              start,
              allDay,
              time: timeRange(start, rawEnd && !allDay ? moment(rawEnd) : null, allDay),
              color: colorByCal[cal.id] || EVENT_BAR_COLORS[0],
            })
          })
        })
      } catch (e) {
        // graceful: no connected calendars / settings -- agenda still renders TeeUps
        // eslint-disable-next-line no-console
        console.warn("[coming-up] calendar events load failed", e)
        if (alive) setRail({ connected: false, syncing: null, displayed: [] })
      }

      // ---- merge + day-group (chronological; all-day rows first within a day) ----
      teeupRows.forEach(t => {
        t.showSync = t.decided && hasWriteTarget
      })
      const all = [...teeupRows, ...eventRows]
      all.sort((a, b) => {
        const dayDiff = a.start.clone().startOf("day") - b.start.clone().startOf("day")
        if (dayDiff !== 0) return dayDiff
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1
        return a.start - b.start
      })
      const grouped = []
      all.forEach(r => {
        const label = dayLabel(r.start)
        const g = grouped[grouped.length - 1]
        if (g && g.label === label) g.items.push(r)
        else grouped.push({ label, items: [r] })
      })
      if (alive) {
        setDays(grouped)
        setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  return (
    <div className="coming-up-agenda" data-testid="coming-up-agenda">
      <div className="coming-up-agenda__main">
        {loading && <div className="coming-up-agenda__empty">Loading…</div>}
        {!loading && days.length === 0 && (
          <div className="coming-up-agenda__empty">Nothing coming up</div>
        )}
        {days.map(g => (
          <div className="coming-up-agenda__group" key={g.label} data-testid="cua-day-group">
            <div className="coming-up-agenda__day" data-testid="cua-day-label">
              {g.label}
            </div>
            {g.items.map(item =>
              item.kind === "teeup" ? (
                <div
                  key={item.id}
                  className="cua-card"
                  data-testid="cua-teeup-card"
                  onClick={() => history.push(`/teeUps/${item.id}`)}>
                  <CardCover src={item.photo} seed={item.id} />
                  <div className="cua-card__body">
                    <div className="cua-card__toprow">
                      <span
                        className={
                          "cua-card__chip " +
                          (item.decided ? "cua-card__chip--allset" : "cua-card__chip--planning")
                        }
                        data-testid="cua-status-chip">
                        {item.decided ? "ALL SET" : "PLANNING"}
                      </span>
                      {item.showSync && <CalSyncIcon />}
                    </div>
                    <div className="cua-card__title">{item.title}</div>
                    <div className="cua-card__meta">
                      {item.dateTime}
                      {item.place ? ` · ${item.place}` : ""}
                    </div>
                    <div className="cua-card__people">
                      {item.participants.map(p => (
                        <img
                          key={p.user_id}
                          className="cua-card__avatar"
                          src={p.avatar_url || ""}
                          alt={p.display_name || ""}
                          title={p.display_name || ""}
                        />
                      ))}
                      {item.participantCount > 0 && (
                        <span className="cua-card__pcount">→ {item.participantCount}</span>
                      )}
                    </div>
                  </div>
                  {item.ownStatus && (
                    <span
                      className={
                        "cua-card__own " +
                        (item.ownStatus === "GOING" ? "cua-card__own--going" : "")
                      }
                      data-testid="cua-own-status">
                      {item.ownStatus}
                    </span>
                  )}
                </div>
              ) : (
                <div key={item.id} className="cua-event" data-testid="cua-event-row">
                  <span className="cua-event__bar" style={{ background: item.color }} />
                  <span className="cua-event__title">{item.title}</span>
                  <span className="cua-event__time">{item.time}</span>
                </div>
              )
            )}
          </div>
        ))}
      </div>

      <aside className="coming-up-agenda__rail" data-testid="cua-right-rail">
        <div className="cua-rail-card">
          <div className="cua-rail-card__title">Connected calendars</div>
          {rail && rail.syncing ? (
            <div className="cua-rail-card__section" data-testid="cua-rail-syncing">
              <div className="cua-rail-card__label">Syncing</div>
              <div className="cua-rail-card__acct-row">
                <ProviderIcon provider={rail.syncing.provider} />
                <div className="cua-rail-card__acct">{rail.syncing.account}</div>
              </div>
              <div className="cua-rail-card__cal">{rail.syncing.calendar}</div>
            </div>
          ) : (
            <div className="cua-rail-card__section">
              <div className="cua-rail-card__label">Syncing</div>
              <div className="cua-rail-card__none">No sync calendar set</div>
            </div>
          )}
          <div className="cua-rail-card__section" data-testid="cua-rail-displayed">
            <div className="cua-rail-card__label">Displayed</div>
            {rail && rail.displayed && rail.displayed.length > 0 ? (
              rail.displayed.map(g => (
                <div key={g.account} className="cua-rail-card__group">
                  <div className="cua-rail-card__acct-row">
                    <ProviderIcon provider={g.provider} />
                    <div className="cua-rail-card__acct">{g.account}</div>
                  </div>
                  {g.calendars.map(c => (
                    <div key={c} className="cua-rail-card__cal">
                      {c}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="cua-rail-card__none">
                {rail && rail.connected
                  ? "No calendars selected for display"
                  : "No calendars connected"}
              </div>
            )}
          </div>
          <button
            type="button"
            className="cua-rail-card__manage"
            data-testid="cua-manage-calendars"
            onClick={() => history.push("/settings?option=calendars")}>
            Manage my calendars
          </button>
        </div>
      </aside>
    </div>
  )
}

export default ComingUpAgenda
